import {
  BusinessInvoicesSummary,
  IntervalSummary,
} from 'lib/api/business/types';
import { MONTH_NAMES } from 'lib/consts';
import { LineChartData } from 'lib/types/shared';

const OMIT_KEYS = [
  'Últimos 3M',
  'Últimos 6M',
  'Últimos 12M',
  'Últimos 24M',
  'Últimos 6M año anterior',
];

const PERIOD_SUFFIXES = {
  current: 'últimos 12M',
  previous: '12-24M',
} as const;

function capitalize(str: string): string {
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function isCurrentYear(month: number, year: number): boolean {
  if (year === new Date().getFullYear()) {
    return true;
  } else {
    if (
      year === new Date().getFullYear() - 1 &&
      month > new Date().getMonth() + 1
    ) {
      return true;
    }
    return false;
  }
}

const generateInitialKeys = (): { [key: string]: number } => {
  const keys: { [key: string]: number } = {};
  Object.values(PERIOD_SUFFIXES).forEach((suffix) => {
    keys[`Compra ${suffix}`] = 0;
    keys[`Venta ${suffix}`] = 0;
    keys[`NC/Reclamo ${suffix}`] = 0;
    keys[
      `Venta real (Venta - NC/Reclamo) ${suffix === PERIOD_SUFFIXES.current ? '12M' : '12-24M'}`
    ] = 0;
  });

  return keys;
};

const getPeriodSuffix = (isCurrent: boolean): string => {
  return isCurrent ? PERIOD_SUFFIXES.current : PERIOD_SUFFIXES.previous;
};

export const chartCalculator = (
  data: BusinessInvoicesSummary,
  identifiers: string[],
  all?: boolean,
): LineChartData => {
  const result: { [key: string]: IntervalSummary[] } = data.dataPayers;
  const finalResult: { [key: string]: { [key: string]: number } } = {};

  for (const [key, value] of Object.entries(result)) {
    if (OMIT_KEYS.includes(key)) continue;

    const month = capitalize(key.split('-')[0]);
    const year = parseInt(key.split('-')[1]);
    const isCurrent = isCurrentYear(MONTH_NAMES.indexOf(month) + 1, year);
    const suffix = getPeriodSuffix(isCurrent);

    if (!finalResult[month]) {
      finalResult[month] = generateInitialKeys();
    }

    for (const entry of value) {
      if (identifiers.includes(entry.identifier) || all) {
        finalResult[month][`Compra ${suffix}`] += entry.buyAmount;
        finalResult[month][`Venta ${suffix}`] += entry.salesAmount;
        finalResult[month][`NC/Reclamo ${suffix}`] +=
          entry.salesCreditNoteAmount;
        const realSalesKey = `Venta real (Venta - NC/Reclamo) ${
          isCurrent ? '12M' : '12-24M'
        }`;
        finalResult[month][realSalesKey] +=
          entry.salesAmount - entry.salesCreditNoteAmount;
      }
    }
  }

  const finalResultArray: LineChartData = Object.entries(finalResult).map(
    ([key, value]) => {
      return {
        name: key,
        ...value,
      };
    },
  );

  const currentMonthIndex = new Date().getMonth();
  const orderedMonthsArray = [
    ...MONTH_NAMES.slice(currentMonthIndex + 1),
    ...MONTH_NAMES.slice(0, currentMonthIndex + 1),
  ];

  finalResultArray.sort(
    (a, b) =>
      orderedMonthsArray.indexOf(a.name) - orderedMonthsArray.indexOf(b.name),
  );
  return finalResultArray;
};
